import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ProductDetailsModal from '../../components/ProductDetailsModal';
import LoginModal from '../../components/LoginModal/LoginModal';
import { useInquiry } from '../../context/InquiryContext';
import { useAuth } from '../../context/AuthContext';
import { productsApi } from '../../services/apiService';
import { getPlanConfig, shouldShowVerifiedBadge } from '../../utils/membershipUtils';
import Pagination from '../../components/Pagination/Pagination';
import './FeaturedProductsPage.css';
import '../HomePage/HomePage.css';

const FeaturedProductsPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [showProductDetailsModal, setShowProductDetailsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addedProducts, setAddedProducts] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // Pagination settings - 5 columns x 3 rows = 15 products per page (matching Figma)
  const productsPerPage = 15;

  // Auth context
  const { isAuthenticated } = useAuth();

  // Inquiry context
  const { addToInquiry, isInInquiry, removeFromInquiry } = useInquiry();

  // Fetch featured products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // Fetch all products since this is the "View All" page
        const response = await productsApi.getAll({ limit: 100 });
        // If response has products array, use it, otherwise use response directly
        const productList = response.products || response || [];
        setProducts(productList);
      } catch (error) {
        console.error('Error fetching featured products:', error);
        // Try featured as fallback
        try {
          const fallbackResponse = await productsApi.getFeatured();
          const productList = fallbackResponse.products || fallbackResponse || [];
          setProducts(productList);
        } catch (fallbackError) {
          console.error('Fallback fetch also failed:', fallbackError);
          setProducts([]);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Handle inquire button click
  const handleInquireClick = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if user is logged in first
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    const seller = product.seller || {};
    const productData = {
      id: product.id,
      image: product.image || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500",
      description: product.description || product.name,
      priceMin: product.priceMin || 0,
      priceMax: product.priceMax || 0,
      priceUnit: 'USD',
      pricePer: product.unit || 'Unit',
      moq: product.moq || '1 Unit',
      companyName: seller.name || seller.companyName || 'Unknown Seller',
      sellerId: seller.userId || product.userId,
      sellerName: seller.name || seller.companyName || 'Unknown Seller',
      sellerAvatar: seller.logo || 'https://via.placeholder.com/52',
      country: seller.country || 'UAE',
      countryFlag: seller.countryFlag || 'https://flagcdn.com/w80/ae.png'
    };
    
    if (isInInquiry(product.id)) {
      removeFromInquiry(product.id);
      setAddedProducts(prev => ({ ...prev, [product.id]: false }));
    } else {
      addToInquiry(productData);
      setAddedProducts(prev => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setAddedProducts(prev => ({ ...prev, [product.id]: 'done' }));
      }, 2000);
    }
  };

  const renderProductCard = (product) => {
    const seller = product.seller || {};
    // Use seller.name as the display name (seller profile has the real company name)
    const displayCompanyName = seller.name || 'Unknown Seller';
    const displayLogo = seller.logo || "https://via.placeholder.com/24x24?text=S";
    
    // Handle card click to navigate to product details
    const handleCardClick = (e) => {
      // Don't navigate if clicking on a button
      if (e.target.closest('button')) return;
      navigate(`/product/${product.id}`);
    };
    
    return (
      <div key={product.id} className="featured-product-card" onClick={handleCardClick} style={{ cursor: 'pointer', height: 'fit-content' }}>
        {/* Product Image Section */}
        <div className="featured-product-image-section">
          <img 
            src={product.image || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500"} 
            alt={product.name} 
            className="featured-product-img"
          />
        </div>
        
        {/* Product Details Section */}
        <div className="featured-product-details-section" style={{ flexGrow: 0 }}>
          {/* Info Block */}
          <div className="featured-info-block">
            {/* Product Name Block */}
            <div className="featured-desc-block">
              <p className="featured-product-name">
                {product.name || 'Unknown Product'}
              </p>
              
              {/* Price Block */}
              <div className="featured-price-block">
                <div className="featured-price-row">
                  <span className="featured-price-amount">${product.priceMin} - ${product.priceMax}</span>
                  <span className="featured-price-unit">/ {product.unit}</span>
                </div>
                <p className="featured-moq">
                  <span className="featured-moq-label">(MOQ) </span>
                  <span className="featured-moq-value">{product.moq}</span>
                </p>
              </div>
            </div>
            
            {/* Badges Block */}
            <div className="featured-badges-block">
              <div className="featured-badges-row">
                {/* Membership Plan Badge - Show seller's actual plan */}
                {(() => {
                  const planConfig = getPlanConfig(seller.membershipPlan);
                  
                  return (
                    <div className="featured-global-badge" style={{ borderColor: planConfig.color }}>
                      <img 
                        src={planConfig.icon} 
                        alt={planConfig.label} 
                        className="featured-global-icon"
                      />
                      <span className="featured-global-text" style={{ color: planConfig.color }}>{planConfig.label}</span>
                    </div>
                  );
                })()}
                
                {/* Verified Badge - only show for GCC and Global plans (not Basic) */}
                {shouldShowVerifiedBadge(seller.membershipPlan) && (
                  <div className="featured-verified-badge">
                    <svg width="16" height="16" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 11C22 11.9323 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5014 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.3129 1.49659 8.4528C1.73472 7.5623 1.02177 6.2779 1.47205 5.49857C1.92954 4.7077 3.40305 4.68605 4.04382 4.04383C4.68605 3.40161 4.70769 1.92955 5.49856 1.47206C6.27788 1.02178 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0C11.9337 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02178 16.5014 1.47206C17.2923 1.92955 17.314 3.40305 17.9562 4.04383C18.5984 4.68461 20.0705 4.7077 20.528 5.49857C20.9782 6.2779 20.2653 7.5623 20.5034 8.4528C20.7329 9.3129 22 10.0663 22 11Z" fill="#29ABE2"/>
                      <path d="M15.5876 7.6015C15.1229 7.1368 14.3696 7.1368 13.9049 7.6015L9.0312 12.4751L7.00786 10.4518C6.54315 9.9871 5.7898 9.9871 5.32509 10.4518C4.86039 10.9165 4.86039 11.6698 5.32509 12.1345L8.14798 14.9574C8.39188 15.2013 8.71372 15.3168 9.0327 15.3052C9.3516 15.3168 9.6734 15.2013 9.9173 14.9574L15.5891 9.2857C16.0538 8.821 16.0538 8.0676 15.5891 7.6029L15.5876 7.6015Z" fill="white"/>
                    </svg>
                    <span className="featured-verified-text">Verified</span>
                  </div>
                )}
              </div>
              
              {/* Seller Company Name - Clickable to go to seller profile */}
              <div 
                className="featured-seller-company"
                onClick={(e) => {
                  e.stopPropagation();
                  if (seller.id) navigate(`/seller/${seller.id}`);
                }}
                style={{ cursor: seller.id ? 'pointer' : 'default' }}
                title={seller.id ? `View ${displayCompanyName} profile` : ''}
              >
                <img 
                  src={displayLogo} 
                  alt={displayCompanyName} 
                  className="featured-company-logo"
                />
                <span className="featured-company-name">{displayCompanyName}</span>
              </div>
              
              {/* Location Row */}
              <div className="featured-location-row">
                <div className="featured-flag-wrapper">
                  <img 
                    src={seller.countryFlag || "https://flagcdn.com/w80/ae.png"} 
                    alt="Flag" 
                    className="featured-flag-img"
                  />
                </div>
                <span className="featured-location-text">
                  <span className="featured-country">{(seller.country || 'UAE').replace('United Arab Emirates', 'UAE')} / </span>
                  <span className="featured-years">{seller.memberYears || 1} Year{(seller.memberYears || 1) > 1 ? 's' : ''}</span>
                </span>
              </div>
              
              {/* Business Type */}
              <span className="featured-business-text">{seller.businessType || 'Manufacturer'}</span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="featured-actions-row">
            <button 
              className={`featured-btn-inquire ${isInInquiry(product.id) ? 'added' : ''}`}
              onClick={(e) => handleInquireClick(product, e)}
            >
              {addedProducts[product.id] === true ? '✓ Added!' : isInInquiry(product.id) ? 'Remove' : 'Add To Quotation'}
            </button>
            <button 
              className="featured-btn-call"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const phone = seller.phone || seller.phoneNumber || product.phone;
                if (phone) {
                  window.location.href = `tel:${phone}`;
                } else {
                  alert('Phone number not available for this seller');
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 16.92V19.92C22 20.4704 21.7893 20.9981 21.4142 21.3981C21.0391 21.7982 20.5304 22.0362 19.98 22.0699C19.643 22.0899 19.2989 22.0999 18.95 22.0999C10.21 22.0999 3.10001 14.9899 3.10001 6.24994C3.10001 5.90094 3.11001 5.55694 3.13001 5.21994C3.16376 4.66947 3.40177 4.16077 3.80183 3.78568C4.20189 3.41059 4.72954 3.19994 5.28001 3.19994H8.28001C8.72424 3.19557 9.15598 3.35553 9.49332 3.64969C9.83067 3.94386 10.0507 4.35171 10.11 4.79194C10.2199 5.66094 10.4293 6.51394 10.73 7.32994C10.8567 7.66661 10.8837 8.03161 10.808 8.38227C10.7323 8.73294 10.5571 9.05427 10.3 9.30994L9.00001 10.6099C10.3612 13.1755 12.4245 15.2387 14.99 16.5999L16.29 15.2999C16.5457 15.0429 16.8671 14.8677 17.2177 14.792C17.5684 14.7163 17.9334 14.7433 18.27 14.8699C19.086 15.1707 19.9391 15.3801 20.808 15.4899C21.2532 15.5498 21.6651 15.7746 21.9605 16.1183C22.256 16.462 22.4131 16.9009 22.4 17.3499L22 16.92Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Call</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination
  const totalPages = Math.ceil(products.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = products.slice(startIndex, endIndex);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate start and end of middle pages
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      if (currentPage <= 2) {
        end = 4;
      } else if (currentPage >= totalPages - 1) {
        start = totalPages - 3;
      }
      
      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (end < totalPages - 1) pages.push('...');
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="featured-products-page">
      <Header />
      
      <main className="featured-main-content">
        <div className="featured-content-wrapper">
          {/* Breadcrumb */}
          <div className="featured-breadcrumb">
            <span className="breadcrumb-link">Home</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="breadcrumb-current">Featured Products</span>
          </div>
          
          {/* Title Section */}
          <div className="featured-title-section">
            <h1 className="featured-page-title">Featured Products</h1>
            <p className="featured-page-subtitle">Top-rated products chosen for quality and value.</p>
          </div>
          
          {/* Products Grid */}
          {loading ? (
            <div className="featured-loading" style={{textAlign: 'center', padding: '40px'}}>
              <p>Loading featured products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="featured-empty" style={{textAlign: 'center', padding: '40px'}}>
              <p>No featured products found.</p>
            </div>
          ) : (
            <div className="featured-products-grid">
              {currentProducts.map(product => renderProductCard(product))}
            </div>
          )}
          
          {/* Pagination - Only show if there are products */}
          {!loading && products.length > 0 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          )}
        </div>
      </main>
      
      <Footer />
      
      {/* Product Details Modal */}
      <ProductDetailsModal 
        isOpen={showProductDetailsModal} 
        onClose={() => {
          setShowProductDetailsModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
      />
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </div>
  );
};

export default FeaturedProductsPage;
